import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { type UserInfoType } from "../models/profile/user-info";
import {
  getBasicUserProfileInfo,
  getInfoBalanceAndTokens,
  type BasicUserProfileInfo,
} from "../utils/user-profile";
import { useAppConfigStore } from "./app-config-store";
import { type NetworkType } from "../models/app-config";
import { type TopNetworksAndTokensType } from "../apis/account-balance";
import { useCallback, useEffect, useMemo } from "react";
import { addressShorten, checkValidAddress } from "../utils/wallet";
import { type UserNFTInfo } from "../hooks/profile/useNFTsByWallet";
import { type PinnedMetaNFT } from "../apis/pin-nft";
import { useGetListPinnedNFTs } from "../hooks/profile/useGetListPinnedNFTs";
import { useConnection } from "wagmi";
import { useParams } from "react-router-dom";
import { useGetAllNickNames } from "../hooks/account-setting/useGetAllNickNames";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "../constants/queryKeys";

interface UserProfileStore {
  userProfile: UserInfoType | null;
  topNetworksAndTokens: TopNetworksAndTokensType;
  pinnedNFTs: UserNFTInfo[];
  pinnedMetaNFT: PinnedMetaNFT | null;
  setUserProfile: (profile: UserInfoType | null) => void;
  setPartialUserProfile: (profile: Partial<UserInfoType> | null) => void;
  setPinnedNFTs: (pinnedNFTs: UserNFTInfo[]) => void;
  setPinnedMetaNFT: (pinnedMetaNFT: PinnedMetaNFT) => void;
  setTopNetworksAndTokens: (
    topNetworksAndTokens: TopNetworksAndTokensType
  ) => void;
  resetState: () => void;
}

const initialState = {
  userProfile: null,
  topNetworksAndTokens: {
    netWorth: "0",
    topTokens: [],
    topNetworks: [],
  },
  pinnedNFTs: [],
  pinnedMetaNFT: null,
};

export const useUserProfileStore = create<UserProfileStore>((set) => ({
  ...initialState,

  setPartialUserProfile: (profile: Partial<UserInfoType> | null) => {
    set((state) => {
      if (!state.userProfile) return { userProfile: null };
      const newUserProfile: UserInfoType = {
        ...state.userProfile,
        ...(profile ?? {}),
      };
      return { userProfile: newUserProfile };
    });
  },

  setUserProfile: (profile: UserInfoType | null) => {
    set({ userProfile: profile });
  },

  setTopNetworksAndTokens: (topNetworksAndTokens: TopNetworksAndTokensType) => {
    set({ topNetworksAndTokens });
  },

  setPinnedNFTs: (pinnedNFTs: UserNFTInfo[]) => {
    set({ pinnedNFTs });
  },

  setPinnedMetaNFT: (pinnedMetaNFT: PinnedMetaNFT) => {
    set({ pinnedMetaNFT });
  },

  resetState: () => {
    set(initialState);
  },
}));

export function useGetBasicUserProfileInfo(): BasicUserProfileInfo | null;
export function useGetBasicUserProfileInfo<T>(
  select: (info: BasicUserProfileInfo | null) => T
): T;
export function useGetBasicUserProfileInfo<T>(
  select?: (info: BasicUserProfileInfo | null) => T
): T | BasicUserProfileInfo | null {
  return useUserProfileStore(
    useShallow((state) => {
      const info = state.userProfile
        ? getBasicUserProfileInfo(state.userProfile)
        : null;
      return select ? select(info) : info;
    })
  );
}

export const useProfileRelation = (): {
  youFollowedOnNetworks: NetworkType[];
  followingYouOnNetworks: NetworkType[];
} | null => {
  const supportNetworks = useAppConfigStore(
    (state) => state.basicConfig.supportNetworks
  );
  const relation = useUserProfileStore((state) => state.userProfile?.relation);

  if (!relation || !supportNetworks) return null;

  const youFollowedOnNetworks =
    supportNetworks?.filter((item) =>
      relation?.followings?.includes(item.chain_id)
    ) ?? [];

  const followingYouOnNetworks =
    supportNetworks?.filter((item) =>
      relation?.followers?.includes(item.chain_id)
    ) ?? [];

  return {
    youFollowedOnNetworks,
    followingYouOnNetworks,
  };
};

export const useTopNetworksAndTokensOfProfile = (address: string): void => {
  const setTopNetworksAndTokens = useUserProfileStore(
    (state) => state.setTopNetworksAndTokens
  );

  const { data } = useQuery({
    queryKey: [QUERY_KEY.accountBalance, address],
    queryFn: async () => await getInfoBalanceAndTokens(address),
    enabled: !!address,
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    setTopNetworksAndTokens(data ?? initialState.topNetworksAndTokens);
  }, [data, setTopNetworksAndTokens]);
};

export const useGetListPinnedNFTsOfProfile = (address: string): void => {
  const isValidAddress = checkValidAddress(address);

  const setPinnedNFTs = useUserProfileStore((state) => state.setPinnedNFTs);
  const setPinnedMetaNFT = useUserProfileStore(
    (state) => state.setPinnedMetaNFT
  );

  const { pinnedNFTs, pinnedMetaNFT, isPending } = useGetListPinnedNFTs(
    address,
    isValidAddress
  );

  useEffect(() => {
    if (!address || isPending) return;
    setPinnedNFTs(pinnedNFTs);
    setPinnedMetaNFT(pinnedMetaNFT);
  }, [
    address,
    isPending,
    pinnedMetaNFT,
    pinnedNFTs,
    setPinnedMetaNFT,
    setPinnedNFTs,
  ]);
};

export const useIsYourProfile = (): boolean => {
  const { address: walletAddress } = useConnection();
  const { address } = useParams<{ address?: string }>();

  const isYourProfile = useMemo(
    () =>
      !!address &&
      walletAddress?.toLowerCase() === String(address)?.toLowerCase(),
    [address, walletAddress]
  );
  return isYourProfile;
};

export const useCurrentProfileAddress = (): string => {
  const { address } = useParams<{ address?: string }>();
  return (address as string) ?? "";
};

export const useGetUserDisplayName = (): {
  getDisplayName: (user: UserInfoType | null) => string;
} => {
  const { data: allNickNames } = useGetAllNickNames();
  const getDisplayName = useCallback(
    (user: UserInfoType | null) => {
      return (
        allNickNames?.get(user?.id)?.name ??
        user?.display_name ??
        addressShorten(user?.wallet_address) ??
        ""
      );
    },
    [allNickNames]
  );

  return { getDisplayName };
};

export const useGetInitPinnedNFTs = (): string[] => {
  const { address } = useConnection();
  const { pinnedNFTs } = useGetListPinnedNFTs(address, !!address);
  const nftIds =
    pinnedNFTs?.map(
      (item: UserNFTInfo) =>
        `${item?.chain_id}_${item?.token_address?.toLowerCase()}_${item?.token_id}`
    ) ?? [];
  return nftIds;
};
